import { useEffect, useMemo, useState } from "react";
import AppHeader from "@/components/AppHeader";
import MobileBottomNav from "@/components/MobileBottomNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { loadSnapshots, deleteSnapshot, clearAllSnapshots } from "@/lib/gallery";
import { Download, Trash2, X, Search, ImageOff } from "lucide-react";
import { toast } from "sonner";

export default function Gallery() {
  const { user } = useAuth();
  const tenantId = user?.tenant_id || "default";
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState("");
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    setItems(loadSnapshots(tenantId));
  }, [tenantId]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((s) =>
      `ch${String(s.channel).padStart(2, "0")} ${s.cameraName} ${s.location} ${s.istDate} ${s.istTime}`
        .toLowerCase()
        .includes(q)
    );
  }, [items, query]);

  // Group by IST date
  const grouped = useMemo(() => {
    const g = new Map();
    filtered.forEach((s) => {
      const arr = g.get(s.istDate) || [];
      arr.push(s);
      g.set(s.istDate, arr);
    });
    return Array.from(g.entries()); // [ [date, items[]], ... ]
  }, [filtered]);

  const removeOne = (id) => {
    const updated = deleteSnapshot(tenantId, id);
    setItems(updated);
    toast.success("Snapshot removed");
  };

  const wipeAll = () => {
    if (!window.confirm("Delete all snapshots from this browser? This cannot be undone.")) return;
    clearAllSnapshots(tenantId);
    setItems([]);
    toast.success("Gallery cleared");
  };

  const downloadThumb = (s) => {
    const a = document.createElement("a");
    a.href = s.thumb;
    a.download = `CH${String(s.channel).padStart(2, "0")}_${s.cameraName.replace(/[^a-z0-9_-]+/gi, "_")}_${s.istDate}_${s.istTime.replace(/:/g, "-")}.jpg`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  return (
    <div className="min-h-screen bg-black text-zinc-100 flex flex-col pb-14 md:pb-0">
      <AppHeader active="gallery" />

      <div className="border-b border-zinc-900 bg-zinc-950 px-3 sm:px-4 py-2 flex items-center gap-2 justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <div className="mono text-[10px] uppercase tracking-widest text-cyan-400">Snapshot Archive</div>
          <div className="mono text-[10px] text-zinc-500 hidden sm:block">
            {items.length} FRAME{items.length !== 1 ? "S" : ""} · Stored on this device
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-500" />
            <Input
              data-testid="gallery-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search camera / date"
              className="mono text-[11px] bg-black border-zinc-800 rounded-none pl-7 h-8 w-40 sm:w-64"
            />
          </div>
          <Button
            data-testid="gallery-clear-all"
            variant="ghost"
            onClick={wipeAll}
            disabled={items.length === 0}
            className="rounded-none h-8 text-zinc-400 hover:text-red-400 border border-zinc-800 mono text-[10px] uppercase tracking-widest"
          >
            <Trash2 size={12} className="mr-1.5" /> Clear
          </Button>
        </div>
      </div>

      <div className="flex-1 p-3 sm:p-6 overflow-auto">
        {filtered.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center gap-3 py-24" data-testid="gallery-empty">
            <ImageOff size={40} className="text-zinc-700" />
            <div className="mono text-[11px] uppercase tracking-widest text-zinc-500">
              {items.length === 0 ? "No snapshots yet" : "No results for that search"}
            </div>
            <div className="mono text-[10px] text-zinc-600 max-w-md">
              Open any camera in expanded view and press the SNAPSHOT button — a copy will be downloaded and archived here for quick review.
            </div>
          </div>
        ) : (
          <div className="space-y-8">
            {grouped.map(([date, dayItems]) => (
              <section key={date} data-testid={`gallery-day-${date}`}>
                <div className="flex items-center justify-between mb-3 border-b border-zinc-900 pb-1">
                  <div className="mono text-[10px] uppercase tracking-widest text-amber-400">{date}</div>
                  <div className="mono text-[9px] uppercase tracking-widest text-zinc-500">
                    {dayItems.length} frame{dayItems.length !== 1 ? "s" : ""}
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                  {dayItems.map((s) => (
                    <div
                      key={s.id}
                      data-testid={`gallery-item-${s.id}`}
                      className="relative border border-zinc-900 hover:border-cyan-500/50 group cursor-pointer bg-zinc-950"
                      onClick={() => setPreview(s)}
                    >
                      <div className="relative aspect-video overflow-hidden">
                        <img src={s.thumb} alt="" className="w-full h-full object-cover" />
                        <div className="absolute top-1 left-1 mono text-[9px] px-1 bg-black/70 text-cyan-300 tracking-widest">
                          CH{String(s.channel).padStart(2, "0")}
                        </div>
                        <div className="absolute bottom-1 right-1 mono text-[9px] px-1 bg-black/70 text-amber-300 tracking-widest">
                          {s.istTime}
                        </div>
                      </div>
                      <div className="p-1.5 border-t border-zinc-900">
                        <div className="mono text-[10px] text-zinc-300 truncate">{s.cameraName}</div>
                        {s.location && <div className="mono text-[9px] text-zinc-500 truncate">{s.location}</div>}
                      </div>
                      <button
                        data-testid={`gallery-delete-${s.id}`}
                        onClick={(e) => { e.stopPropagation(); removeOne(s.id); }}
                        className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity bg-black/80 hover:bg-red-500/40 border border-red-500/50 text-red-400 p-1"
                        title="Remove from gallery"
                      >
                        <Trash2 size={11} />
                      </button>
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>

      {/* Preview modal */}
      <Dialog open={!!preview} onOpenChange={(v) => !v && setPreview(null)}>
        <DialogContent
          className="rounded-none bg-black border-zinc-800 text-zinc-100 max-w-4xl p-0 [&>button.absolute]:hidden"
          data-testid="gallery-preview"
        >
          <DialogTitle className="sr-only">
            {preview ? `Snapshot preview — CH${String(preview.channel).padStart(2, "0")} ${preview.cameraName}` : "Snapshot preview"}
          </DialogTitle>
          {preview && (
            <div className="flex flex-col">
              <div className="flex items-center justify-between px-4 py-2 border-b border-zinc-900 gap-3">
                <div className="mono text-[10px] uppercase tracking-widest text-cyan-400 truncate">
                  CH{String(preview.channel).padStart(2, "0")} · {preview.cameraName} · {preview.istDate} {preview.istTime} IST
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button
                    data-testid="gallery-preview-download"
                    variant="ghost"
                    onClick={() => downloadThumb(preview)}
                    className="rounded-none h-7 px-2 text-cyan-400 border border-cyan-500/40 hover:bg-cyan-500/15 mono text-[10px] uppercase tracking-widest"
                  >
                    <Download size={11} className="mr-1.5" /> JPG
                  </Button>
                  <Button
                    data-testid="gallery-preview-close"
                    variant="ghost"
                    onClick={() => setPreview(null)}
                    className="rounded-none h-7 w-7 p-0 text-zinc-400 border border-zinc-800 hover:text-red-400 hover:border-red-500/50"
                    title="Close"
                  >
                    <X size={14} />
                  </Button>
                </div>
              </div>
              <div className="bg-black">
                <img src={preview.thumb} alt="" className="w-full h-auto max-h-[70vh] object-contain" />
              </div>
              <div className="px-4 py-2 border-t border-zinc-900 mono text-[9px] text-zinc-600 uppercase tracking-widest">
                Stored locally. Full-resolution PNG was downloaded at capture time.
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <MobileBottomNav active="gallery" />
    </div>
  );
}
